/**
 * Clerk bootstrap.
 *
 * The publishable key is not known at build time: it is fetched from the
 * worker (GET /api/auth/config) before <ClerkProvider> mounts. The Frontend
 * API host (clerk.myleadsyncer.com in production) is derived from the key.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ClerkProvider } from '@clerk/react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { authApi } from './api';
import { Spinner } from '../components/ui';
import { useTheme } from './theme';

/**
 * Clerk theming. Clerk derives hover/border/shade colours from these values, so they must
 * be real colours: `var(--x)` references leave its buttons and input borders blank. The
 * values mirror the light/dark tokens in src/index.css; keep them in sync.
 */
const PALETTE = {
  light: {
    primary: '#0169C6', danger: '#E11D48', success: '#3E9431', warning: '#B7791F',
    ink: '#0B1F3A', ink2: '#4B5563', surface: '#FFFFFF', surface2: '#F2F7FB', border: '#DDEAF2', accentInk: '#014A9B',
  },
  dark: {
    primary: '#3B97E6', danger: '#FB7185', success: '#6FCC5E', warning: '#FBBF24',
    ink: '#EAF2FB', ink2: '#93A6BE', surface: '#0E1B2E', surface2: '#0A1524', border: '#1B2C45', accentInk: '#7FC0F0',
  },
};

function clerkAppearance(theme: 'light' | 'dark') {
  const p = PALETTE[theme];
  return {
    variables: {
      colorPrimary: p.primary,
      colorPrimaryForeground: '#FFFFFF',
      colorDanger: p.danger,
      colorSuccess: p.success,
      colorWarning: p.warning,
      colorForeground: p.ink,
      colorMutedForeground: p.ink2,
      colorMuted: p.surface2,
      colorBackground: p.surface,
      colorInput: p.surface,
      colorInputForeground: p.ink,
      colorNeutral: p.ink,
      colorBorder: p.border,
      colorRing: p.primary,
      colorModalBackdrop: 'rgba(6, 16, 29, 0.55)',
      fontFamily: 'Manrope, system-ui, sans-serif',
      fontFamilyButtons: 'Manrope, system-ui, sans-serif',
      borderRadius: '11px',
    },
    elements: {
      cardBox: { boxShadow: 'var(--shadow)', border: `1px solid ${p.border}`, borderRadius: '18px' },
      headerTitle: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, letterSpacing: '-0.01em' },
      formButtonPrimary: { fontWeight: 700 },
      // Explicit outlines: on a white card the default input/OTP/social borders are invisible.
      // Field outlines are set in src/index.css (.cl-formFieldInput etc.): Clerk's own
      // box-shadow outline wins over appearance.elements and is near-invisible on white.
      formFieldInput: { backgroundColor: p.surface2 },
      otpCodeFieldInput: { backgroundColor: p.surface2, color: p.ink },
      footerActionLink: { color: p.accentInk, fontWeight: 700 },
    },
  };
}

interface ClerkBootProps {
  children: React.ReactNode;
  /** Client-side navigation for Clerk's components (path routing). */
  routerPush: (to: string) => void;
  routerReplace: (to: string) => void;
}

export function ClerkBoot({ children, routerPush, routerReplace }: ClerkBootProps) {
  const { theme } = useTheme();
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setFailed(false);
    const res = await authApi.getConfig();
    if (res.success && res.data?.publishableKey) setPublishableKey(res.data.publishableKey);
    else setFailed(true);
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (failed) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-6">
        <div className="w-full max-w-[400px] bg-surface border border-line rounded-[18px] shadow-card p-7 text-center animate-fade">
          <div className="mx-auto w-11 h-11 rounded-[12px] bg-warning-soft text-warning flex items-center justify-center">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <h1 className="mt-4 font-display font-bold text-[20px] text-ink tracking-tight">We couldn't reach My Lead Syncer</h1>
          <p className="mt-2 text-[13.5px] font-medium text-ink-2">
            The sign-in service didn't respond. Check your connection and try again.
          </p>
          <button onClick={() => void load()} className="ds-btn ds-btn-primary mt-5 mx-auto">
            <RotateCw className="w-4 h-4" /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (!publishableKey) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      appearance={clerkAppearance(theme === 'dark' ? 'dark' : 'light')}
      routerPush={routerPush}
      routerReplace={routerReplace}
      signInUrl="/login"
      signUpUrl="/register"
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/billing"
      afterSignOutUrl="/login"
    >
      {children}
    </ClerkProvider>
  );
}
